import React, { useState } from "react";
import { StorageService } from "../../services/storage.js";
import { resolveImageUrl } from "../../utils/imageStorage.js";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2, MessageCircle, AlertCircle } from "lucide-react";

export const ContactPage = ({ settings, lang, pageData }) => {
  const [formData, setFormData] = useState({
    type: "admission",
    full_name: "",
    phone: "",
    email: "",
    student_name: "",
    student_age: "",
    class_applying: "Noorani & Play/Nursery Group",
    subject: "",
    message: ""
  });
  const [status, setStatus] = useState("idle");
  const [submittedId, setSubmittedId] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.full_name.trim() || !formData.phone.trim() || !formData.message.trim()) {
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setTimeout(() => {
      try {
        const sub = StorageService.saveSubmission({
          type: formData.type,
          full_name: formData.full_name,
          email: formData.email || "not-provided@baitulmuqaddas.local",
          phone: formData.phone,
          student_name: formData.student_name,
          student_age: formData.student_age,
          class_applying: formData.class_applying,
          subject: formData.subject || (formData.type === "admission" ? "New Admission Inquiry 2026" : "General Contact"),
          message: formData.message,
          status: "new"
        });
        setSubmittedId(sub.id);
        setStatus("success");
        setFormData({
          type: "admission",
          full_name: "",
          phone: "",
          email: "",
          student_name: "",
          student_age: "",
          class_applying: "Noorani & Play/Nursery Group",
          subject: "",
          message: ""
        });
      } catch (err) {
        setStatus("error");
      }
    }, 400);
  };

  return (
    <div className="py-12 bg-stone-50 min-h-screen space-y-12">
      {/* Header Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-emerald-950/80 backdrop-blur-md text-white rounded-2xl p-8 sm:p-12 relative overflow-hidden shadow-sm">
          {pageData?.hero_image && (
            <div className="absolute inset-0 z-0">
              <img
                src={pageData.hero_image}
                alt="Contact Banner"
                className="w-full h-full object-cover opacity-25"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 bg-emerald-950/65" />
            </div>
          )}
          <div className="max-w-3xl space-y-3 relative z-10">
            <span className="text-amber-400 font-bold text-xs uppercase tracking-widest block font-sans">
              {lang === "bn" ? pageData?.title_bn || "ভর্তি ও সাধারণ যোগাযোগ" : pageData?.title || "Admissions & Campus Contact"}
            </span>
            <h1 className="text-3xl sm:text-5xl font-serif font-bold tracking-tight text-balance">
              {lang === "bn" ? pageData?.subtitle_bn || "আমাদের সাথে যোগাযোগ করুন" : pageData?.subtitle || "Get in Touch with Us"}
            </h1>
            <p className="text-sm sm:text-base text-emerald-100/90 leading-relaxed font-sans">
              {lang === "bn" ? pageData?.content_bn || "২০২৬ শিক্ষাবর্ষে আপনার সন্তানের ভর্তি সংক্রান্ত যে কোনো তথ্যের জন্য নিচের ফরমটি পূরণ করুন অথবা সরাসরি ক্যাম্পাসে চলে আসুন।" : pageData?.content || "Have questions regarding admissions, curriculum, boarding, or tuition? Complete our form or visit our campus in Feni."}
            </p>
          </div>
        </div>
      </div>

      {/* Main Form and Campus Card Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left: Contact & Admission Form */}
          <div className="lg:col-span-7 bg-white p-6 sm:p-10 rounded-2xl border border-stone-200 shadow-xs space-y-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
                {lang === "bn" ? "ভর্তি ও অনুসন্ধান ফরম" : "Admission & Contact Inquiry Form"}
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                {lang === "bn" ? "ফরমটি পূরণ করলে আমাদের দায়িত্বশীল শিক্ষক আপনার সাথে ফোনে যোগাযোগ করবেন।" : "Submit your details and our admission officer will follow up with you promptly."}
              </p>
            </div>

            {status === "success" && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-900 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                <div className="text-xs space-y-1">
                  <strong className="block font-bold text-sm text-emerald-950">
                    {lang === "bn" ? "আলহামদুলিল্লাহ! আপনার বার্তাটি জমা হয়েছে।" : "Alhamdulillah! Submission Received."}
                  </strong>
                  <p>
                    {lang === "bn"
                      ? `রেফারেন্স আইডি: #${submittedId}। মাদ্রাসার অফিস ডেস্ক অতি দ্রুত আপনার দেওয়া নম্বরে যোগাযোগ করবে।`
                      : `Reference ID: #${submittedId}. Our office desk will review your submission and contact you soon.`}
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="text-emerald-800 font-bold underline pt-1 cursor-pointer"
                  >
                    {lang === "bn" ? "আরেকটি আবেদন পাঠান" : "Submit another inquiry"}
                  </button>
                </div>
              </div>
            )}

            {status === "error" && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-900 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
                <div className="text-xs">
                  <strong className="block font-bold">
                    {lang === "bn" ? "অনুগ্রহ করে আবশ্যকীয় তথ্যগুলো প্রদান করুন।" : "Please check required fields."}
                  </strong>
                  <span>
                    {lang === "bn" ? "অভিভাবকের নাম, ফোন নম্বর এবং বার্তা লেখা আবশ্যক।" : "Name, phone number, and message are required."}
                  </span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                  {lang === "bn" ? "আবেদনের ধরন" : "Inquiry Purpose"}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "admission", labelBn: "নতুন ভর্তি আবেদন", labelEn: "New Admission" },
                    { id: "contact", labelBn: "সাধারণ বার্তা", labelEn: "General Contact" },
                    { id: "inquiry", labelBn: "হিফজ / হোস্টেল", labelEn: "Hifz / Boarding" }
                  ].map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, type: t.id })}
                      className={`py-2 px-3 text-xs font-semibold rounded-lg border transition-all cursor-pointer ${
                        formData.type === t.id
                          ? "bg-emerald-900 text-white border-emerald-900 shadow-xs"
                          : "bg-stone-50 text-slate-700 border-stone-200 hover:bg-stone-100"
                      }`}
                    >
                      {lang === "bn" ? t.labelBn : t.labelEn}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    {lang === "bn" ? "অভিভাবকের পুরো নাম *" : "Guardian Full Name *"}
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder={lang === "bn" ? "যেমন: মোহাম্মদ আব্দুল্লাহ" : "e.g. Mohammad Abdullah"}
                    className="w-full px-3.5 py-2 text-xs rounded-lg border border-stone-300 focus:outline-none focus:ring-1 focus:ring-emerald-700"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    {lang === "bn" ? "মোবাইল / হোয়াটসঅ্যাপ নম্বর *" : "Phone / WhatsApp Number *"}
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="018XXXXXXXX"
                    className="w-full px-3.5 py-2 text-xs rounded-lg border border-stone-300 focus:outline-none focus:ring-1 focus:ring-emerald-700"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    {lang === "bn" ? "ইমেইল ঠিকানা (ঐচ্ছিক)" : "Email Address (Optional)"}
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="example@gmail.com"
                    className="w-full px-3.5 py-2 text-xs rounded-lg border border-stone-300 focus:outline-none focus:ring-1 focus:ring-emerald-700"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    {lang === "bn" ? "আবেদনের বিষয়" : "Subject"}
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder={lang === "bn" ? "যেমন: ২০২৬ সেশনে ভর্তি" : "e.g. 2026 Session Admission"}
                    className="w-full px-3.5 py-2 text-xs rounded-lg border border-stone-300 focus:outline-none focus:ring-1 focus:ring-emerald-700"
                  />
                </div>
              </div>

              {formData.type === "admission" && (
                <div className="p-4 bg-stone-50 rounded-xl border border-stone-200 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        {lang === "bn" ? "শিক্ষার্থীর নাম" : "Student's Name"}
                      </label>
                      <input
                        type="text"
                        value={formData.student_name}
                        onChange={(e) => setFormData({ ...formData, student_name: e.target.value })}
                        placeholder={lang === "bn" ? "শিক্ষার্থীর নাম লিখুন" : "Student Name"}
                        className="w-full px-3.5 py-2 text-xs rounded-lg border border-stone-300 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-700"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        {lang === "bn" ? "বয়স" : "Age"}
                      </label>
                      <input
                        type="text"
                        value={formData.student_age}
                        onChange={(e) => setFormData({ ...formData, student_age: e.target.value })}
                        placeholder="e.g. 6 years"
                        className="w-full px-3.5 py-2 text-xs rounded-lg border border-stone-300 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-700"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      {lang === "bn" ? "আবেদনের শ্রেণি" : "Class Applying For"}
                    </label>
                    <select
                      value={formData.class_applying}
                      onChange={(e) => setFormData({ ...formData, class_applying: e.target.value })}
                      className="w-full px-3.5 py-2 text-xs rounded-lg border border-stone-300 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-700"
                    >
                      <option value="Noorani & Play/Nursery Group">নূরানী ও শিশু শ্রেণি (Play / Nursery / KG)</option>
                      <option value="Class 1 (Primary Model)">১ম শ্রেণি (Class 1)</option>
                      <option value="Class 2 (Primary Model)">২য় শ্রেণি (Class 2)</option>
                      <option value="Class 3 (Primary Model)">৩য় শ্রেণি (Class 3)</option>
                      <option value="Class 4 (Primary Model)">৪র্থ শ্রেণি (Class 4)</option>
                      <option value="Class 5 (Primary Model)">৫ম শ্রেণি (Class 5)</option>
                      <option value="Tahfizul Quran Department">তাহফিজুল কুরআন বিভাগ (হিফজুল কুরআন)</option>
                    </select>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  {lang === "bn" ? "আপনার বার্তা বা বিস্তারিত তথ্য *" : "Message or Notes *"}
                </label>
                <textarea
                  rows={4}
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder={lang === "bn" ? "আপনার জিজ্ঞাসা বা মন্তব্য লিখুন..." : "Write your inquiry or questions here..."}
                  className="w-full px-3.5 py-2 text-xs rounded-lg border border-stone-300 focus:outline-none focus:ring-1 focus:ring-emerald-700"
                />
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full py-3 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                <span>
                  {status === "submitting" ? (lang === "bn" ? "জমা হচ্ছে..." : "Submitting...") : (lang === "bn" ? "আবেদনটি জমা দিন" : "Submit Application")}
                </span>
              </button>
            </form>
          </div>

          {/* Right: Campus Info Card & Location Details */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 shadow-xs space-y-6">
              <div>
                <span className="text-emerald-800 font-bold text-xs uppercase tracking-wider block mb-1">
                  {lang === "bn" ? "ক্যাম্পাস পরিচিতি" : "Direct Campus Visit"}
                </span>
                <h3 className="text-xl font-serif font-bold text-slate-900">
                  {lang === "bn" ? settings?.campus_office_title_bn || "ক্যাম্পাস অফিস ও অবস্থান" : settings?.campus_office_title || "Campus Office & Location"}
                </h3>
              </div>

              <div className="space-y-4 text-xs text-slate-700">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-emerald-800 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block font-bold text-slate-900">
                      {lang === "bn" ? "ঠিকানা ও অবস্থান:" : "Location Address:"}
                    </strong>
                    <span>{lang === "bn" ? settings?.address_bn : settings?.address}</span>
                    <span className="block text-slate-500 text-[11px] mt-0.5">
                      {lang === "bn" ? settings?.campus_office_landmark_bn : settings?.campus_office_landmark}
                    </span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-emerald-800 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block font-bold text-slate-900">
                      {lang === "bn" ? "টেলিফোন ও কল ডেস্ক:" : "Direct Phone & Desk:"}
                    </strong>
                    <span>{settings?.phone}</span>
                    {settings?.emergency_phone && (
                      <span className="block text-slate-500 text-[11px]">
                        {lang === "bn" ? `জরুরি: ${settings.emergency_phone}` : `Emergency: ${settings.emergency_phone}`}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-emerald-800 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block font-bold text-slate-900">
                      {lang === "bn" ? "পরিদর্শন ও অফিস সময়:" : "Visiting & Office Hours:"}
                    </strong>
                    <span>
                      {lang === "bn"
                        ? settings?.campus_office_hours_bn || "শনিবার - বৃহস্পতিবার: সকাল ৮:০০ - বিকাল ৫:০০ (শুক্রবার বন্ধ)"
                        : settings?.campus_office_hours || "Saturday – Thursday: 8:00 AM – 5:00 PM (Friday Closed)"}
                    </span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-emerald-800 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block font-bold text-slate-900">
                      {lang === "bn" ? "ইমেইল:" : "Official Email:"}
                    </strong>
                    <span>{settings?.email}</span>
                  </div>
                </div>
              </div>

              {/* Direct WhatsApp Action Button */}
              <div className="pt-2 border-t border-stone-100">
                <a
                  href={`https://wa.me/${(settings?.whatsapp || "8801712345678").replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-xs transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>{lang === "bn" ? "হোয়াটসঅ্যাপে সরাসরি কথা বলুন" : "Chat via WhatsApp"}</span>
                </a>
              </div>
            </div>

            {/* Campus Photo Card */}
            <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-sm relative h-56">
              <img
                src={resolveImageUrl(
                  pageData?.photos?.find((p) => p.key === "office")?.url || pageData?.photos?.find((p) => p.key === "hero")?.url || pageData?.hero_image || "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg"
                )}
                alt="Baitul Muqaddas Model Madrasah Building"
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end p-4">
                <div className="text-white text-xs">
                  <span className="font-bold block text-amber-300">
                    {lang === "bn" ? settings?.site_name_bn : settings?.site_name}
                  </span>
                  <span className="text-[11px] text-white/80">
                    {lang === "bn" ? settings?.address_bn : settings?.address}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
